import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { ASSETS, STREAM_LINKS } from "@/data/content";

const LINKS = [
  { id: "overview", label: "Overview" },
  { id: "catalog", label: "Catalog" },
  { id: "artists", label: "Artists" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 px-4 sm:px-6 lg:px-10"
    >
      <div
        className={`mx-auto max-w-7xl mt-3 flex items-center justify-between rounded-full border px-4 sm:px-6 py-2.5 transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled
            ? "border-gray-200 bg-white/70 backdrop-blur-2xl shadow-sm"
            : "border-transparent bg-white/0"
        }`}
      >
        <button
          data-testid="nav-logo"
          onClick={() => go("overview")}
          className="flex items-center gap-2.5"
          aria-label="A Flat Records home"
        >
          <img src={ASSETS.logoTransparent} alt="A Flat Records" className="h-6 w-auto" />
        </button>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="px-4 py-2 text-sm text-neutral-600 hover:text-black rounded-full hover:bg-neutral-100 transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={STREAM_LINKS.streamNow}
            target="_blank"
            rel="noreferrer"
            data-testid="stream-cta"
            className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-black px-5 py-2.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-neutral-800"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-playing animate-pulse" />
            Stream Now
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white/70 backdrop-blur"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="md:hidden mx-auto max-w-7xl mt-2 rounded-3xl border border-gray-200 bg-white/90 backdrop-blur-2xl p-3 shadow-lg"
          >
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => go(l.id)}
                className="block w-full text-left px-4 py-3 rounded-2xl text-neutral-700 hover:bg-neutral-100"
              >
                {l.label}
              </button>
            ))}
            <a
              href={STREAM_LINKS.streamNow}
              target="_blank"
              rel="noreferrer"
              className="mt-1 block rounded-2xl bg-black px-4 py-3 text-center text-white"
            >
              Stream Now
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
